import { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLoyaltyTiers } from "../../services/loyalty";
import { FormField, TextInput, TextArea } from "../ui/FormField";
import ImageUpload from "../ui/ImageUpload";

const today = new Date().toISOString().split("T")[0];

const rewardSchema = z.object({
  name: z.string().min(1, "Please enter a reward name"),
  description: z.string().min(1, "Please enter a description"),
  expiry_date: z
    .string()
    .min(1, "Please select an expiry date")
    .refine((v) => v >= today, "Expiry date must be today or in the future"),
  points: z
    .number({ error: "Please enter the points value" })
    .int("Must be a whole number")
    .min(1, "Must be a positive whole number"),
  loyalty_tier_id: z
    .number({ error: "Please select a loyalty tier" })
    .min(1, "Please select a loyalty tier"),
});

type RewardSchema = z.infer<typeof rewardSchema>;

export interface RewardFormValues {
  name: string;
  description: string;
  expiry_date: string;
  points: number;
  loyalty_tier_id: number;
  image_url: string;
}

interface RewardFormProps {
  initialValues?: Partial<RewardFormValues>;
  isPending?: boolean;
  onSave: (values: RewardFormValues) => void;
  onReset?: () => void;
}

const RewardForm = ({ initialValues, isPending, onSave, onReset }: RewardFormProps) => {
  const [imageUrl, setImageUrl] = useState(initialValues?.image_url ?? "");
  const [imageError, setImageError] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const { data: loyaltyTiers, isLoading: tiersLoading } = useLoyaltyTiers();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RewardSchema>({
    resolver: zodResolver(rewardSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
      points: initialValues?.points,
      loyalty_tier_id: initialValues?.loyalty_tier_id,
    },
  });

  const enteredPoints = watch("points");
  const selectedTierId = watch("loyalty_tier_id");

  const filteredTiers = useMemo(() => {
    if (!loyaltyTiers) return [];
    if (!enteredPoints || isNaN(enteredPoints)) return loyaltyTiers;
    return loyaltyTiers.filter((tier) => {
      const maxPoints = tier.max_points || Infinity;
      return maxPoints >= enteredPoints - 1;
    });
  }, [loyaltyTiers, enteredPoints]);

  useEffect(() => {
    if (!loyaltyTiers) return;
    if (selectedTierId && !filteredTiers.some((t) => Number(t.id) === selectedTierId)) {
      setValue("loyalty_tier_id", undefined as unknown as number);
    }
  }, [loyaltyTiers, filteredTiers, selectedTierId, setValue]);

  const onSubmit = (data: RewardSchema) => {
    if (!imageUrl) {
      setImageError("Image is required");
      return;
    }
    onSave({
      name: data.name,
      description: data.description,
      expiry_date: new Date(data.expiry_date).toISOString(),
      points: data.points,
      loyalty_tier_id: data.loyalty_tier_id,
      image_url: imageUrl,
    });
  };

  const handleReset = () => {
    setImageUrl(initialValues?.image_url ?? "");
    setImageError("");
    setUploadKey((k) => k + 1);
    reset({
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
      points: initialValues?.points,
      loyalty_tier_id: initialValues?.loyalty_tier_id,
    });
    onReset?.();
  };

  const inputClass =
    "w-full rounded-[6px] px-4 py-[13px] text-[14px] font-normal outline-none bg-surface-input text-content-body placeholder:text-content-muted";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      <FormField label="Reward Name" error={errors.name?.message}>
        <TextInput {...register("name")} placeholder="e.g. Free Coffee" />
      </FormField>

      <FormField label="Reward Description" error={errors.description?.message}>
        <TextArea {...register("description")} placeholder="Describe the reward…" rows={3} />
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Expiry Date" error={errors.expiry_date?.message}>
          <TextInput type="date" {...register("expiry_date")} min={today} />
        </FormField>
        <FormField label="Points" error={errors.points?.message}>
          <TextInput
            type="number"
            {...register("points", { valueAsNumber: true })}
            placeholder="e.g. 850"
            min={1}
          />
        </FormField>
      </div>

      <FormField label="Loyalty Tier" error={errors.loyalty_tier_id?.message as string}>
        <Controller
          name="loyalty_tier_id"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
              className={inputClass}
              disabled={tiersLoading}
            >
              <option value="">Select a tier…</option>
              {filteredTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name} ({tier.min_points.toLocaleString()} – {tier.max_points ? tier.max_points.toLocaleString() : "upwards"}) points
                </option>
              ))}
            </select>
          )}
        />
      </FormField>

      <ImageUpload
        key={uploadKey}
        label="Reward Image"
        initialUrl={initialValues?.image_url}
        onKeyChange={(key) => { setImageUrl(key); if (key) setImageError(""); }}
        onBusyChange={setIsImageUploading}
      />
      {imageError && <span className="text-[11px] text-content-error">{imageError}</span>}

      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isPending || isImageUploading}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default RewardForm;
